import {
  Book,
  BookOpen,
  Boxes,
  Building2,
  Cpu,
  FileText,
  FolderTree,
  GraduationCap,
  Grid3x3,
  Heart,
  Hospital,
  LayoutDashboard,
  LogOut,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  Radio,
  Rss,
  ScanLine,
  Sprout,
  Stethoscope,
  Store,
  Tag,
  User,
  Users,
  Warehouse,
  Wheat,
} from 'lucide-react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { useSidebar } from '@/contexts/SidebarContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useTenantSwitcher } from '@/contexts/TenantSwitcherContext';
import { useIndustryTranslation } from '@/hooks/useIndustryTranslation';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';

function getInitials(email?: string | null): string {
  if (!email) return '?';
  return email.slice(0, 2).toUpperCase();
}

// Get industry-specific icon for a route
function getIndustryIcon(
  route: string,
  industry?: string | null,
): React.ComponentType<{ className?: string }> {
  const iconMap: Record<
    string,
    Record<string, React.ComponentType<{ className?: string }>>
  > = {
    items: {
      education: GraduationCap,
      healthcare: Heart,
      library: Book,
      agriculture: Wheat,
      retail: Package,
      warehouse: Package,
      default: Boxes,
    },
    shelves: {
      education: BookOpen,
      library: BookOpen,
      warehouse: Warehouse,
      retail: Grid3x3,
      default: Rss,
    },
    branches: {
      education: Building2,
      healthcare: Hospital,
      retail: Store,
      warehouse: Warehouse,
      default: Building2,
    },
    categories: {
      education: FileText,
      library: BookOpen,
      retail: Tag,
      healthcare: Stethoscope,
      agriculture: Sprout,
      warehouse: FolderTree,
      default: FolderTree,
    },
    scanners: {
      default: ScanLine,
    },
    detections: {
      default: Radio,
    },
  };

  const routeIcons = iconMap[route];
  if (!routeIcons) return Boxes;

  const industryKey = industry || 'default';
  return routeIcons[industryKey] || routeIcons.default || Boxes;
}

// Helper component to use useIndustryTranslation hook
function NavigationItem({
  to,
  labelKey,
  icon: Icon,
  isSidebarOpen,
  onNavigate,
}: {
  to: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  isSidebarOpen: boolean;
  onNavigate: () => void;
}): JSX.Element {
  const label = useIndustryTranslation(labelKey);

  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md px-3 py-2 text-muted-foreground transition-colors',
          isActive
            ? 'bg-primary text-primary-foreground hover:bg-primary/90 dark:bg-[hsl(var(--primary-active))] dark:hover:bg-[hsl(var(--primary-hover))]'
            : 'hover:bg-muted hover:text-foreground',
          !isSidebarOpen && 'lg:justify-center lg:px-2',
        )
      }
      title={!isSidebarOpen ? label : undefined}
    >
      <Icon className="h-4 w-4 flex-shrink-0" />
      {isSidebarOpen && <span>{label}</span>}
    </NavLink>
  );
}

export function Sidebar(): JSX.Element {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { activeTenant } = useTenantSwitcher();
  const { isSidebarOpen, closeSidebar, toggleSidebar } = useSidebar();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const logoHorizontalSrc = isDark ? '/brand/trushelf-horizontal-white.svg' : '/brand/trushelf-horizontal.svg';
  const logoIsoSrc = isDark ? '/brand/trushelf-iso-white.svg' : '/brand/trushelf-iso.svg';
  const navigate = useNavigate();
  const isRoot = user?.role === 'root';
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);

  const handleLogout = (): void => {
    setIsLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = async (): Promise<void> => {
    await logout();
    setIsLogoutDialogOpen(false);
    navigate('/login', { replace: true });
  };

  const displayName = user?.firstName?.trim() || user?.email;

  // Routes hidden from user role (no dashboard, stores/branches, scanners, detections)
  const userRoleRestrictedPaths = useMemo(
    () => new Set(['/dashboard', '/branches', '/scanners', '/detections']),
    [],
  );

  // Resolve navigation for tenant based on industry
  const resolvedNavigation = useMemo(() => {
    const routeMap: Record<string, { to: string; route: string }> = {
      branches: { to: '/branches', route: 'branches' },
      categories: { to: '/categories', route: 'categories' },
      shelves: { to: '/shelves', route: 'shelves' },
      items: { to: '/items', route: 'items' },
      scanners: { to: '/scanners', route: 'scanners' },
      detections: { to: '/detections', route: 'detections' },
    };

    const baseNavigation = [
      { to: '/dashboard', labelKey: 'navigation.dashboard', icon: LayoutDashboard },
    ];

    const routeNavigation = Object.values(routeMap).map((item) => ({
        to: item.to,
        labelKey: `navigation.${item.route}`,
        icon: getIndustryIcon(item.route, activeTenant?.industry),
      }));

    const all = [...baseNavigation, ...routeNavigation];
    const isUserRole = user?.role === 'user';
    if (!isUserRole) return all;
    return all.filter((item) => !userRoleRestrictedPaths.has(item.to));
  }, [activeTenant, user?.role, userRoleRestrictedPaths]);

  const tenantNavigation = resolvedNavigation;

  const tenantSharedNavigation =
    isRoot || user?.role === 'admin'
      ? [{ to: '/users', labelKey: 'navigation.users', icon: Users }]
      : [];

  const showTenantNavigation = Boolean(activeTenant) || user?.role !== 'root';

  const navigationItems = showTenantNavigation
    ? [...tenantNavigation, ...tenantSharedNavigation]
    : [
        { to: '/tenants', labelKey: 'navigation.tenants', icon: Store },
        ...(isRoot ? [{ to: '/scanners', labelKey: 'navigation.scanners', icon: ScanLine }] : []),
        ...(isRoot ? [{ to: '/device-models', labelKey: 'navigation.deviceModels', icon: Cpu }] : []),
        ...(isRoot ? [{ to: '/users', labelKey: 'navigation.users', icon: Users }] : []),
      ];

  return (
    <>
      {/* Mobile overlay */}
      {isSidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-background/60 backdrop-blur lg:hidden"
          onClick={closeSidebar}
          aria-label="Close sidebar"
        />
      )}
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex flex-col border-r bg-card shadow-lg transition-all duration-300 lg:static lg:h-full lg:shadow-sm',
          // Mobile: slide in/out
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop: always visible, but width changes
          'lg:translate-x-0',
          isSidebarOpen ? 'w-64' : 'lg:w-16',
        )}
      >
        {/* Toggle button on right edge */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-0 top-4 z-50 hidden h-8 w-8 translate-x-1/2 rounded-full border border-border bg-background shadow-md transition-all hover:bg-accent hover:text-accent-foreground lg:flex"
          onClick={toggleSidebar}
          aria-label={t('header.toggleNavigation')}
        >
          {isSidebarOpen ? (
            <PanelLeftClose className="h-4 w-4" />
          ) : (
            <PanelLeftOpen className="h-4 w-4" />
          )}
        </Button>
        <div className={cn('flex h-16 items-center px-6', !isSidebarOpen && 'lg:justify-center lg:px-2')}>
          {isSidebarOpen ? (
            <img
              src={logoHorizontalSrc}
              alt="TruShelf"
              className="h-5"
            />
          ) : (
            <img
              src={logoIsoSrc}
              alt="TruShelf"
              className="hidden h-8 w-8 lg:block"
            />
          )}
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4 text-sm">
          {navigationItems.map((item) => (
            <NavigationItem
              key={item.to}
              to={item.to}
              labelKey={item.labelKey}
              icon={item.icon}
              isSidebarOpen={isSidebarOpen}
              onNavigate={() => {
                // Close sidebar on mobile when navigating
                if (window.innerWidth < 1024) {
                  closeSidebar();
                }
              }}
            />
          ))}
        </nav>
        <div className="flex-shrink-0 border-t p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  'group w-full gap-2 rounded-md px-3 py-2 text-muted-foreground transition-colors hover:bg-accent/90 hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  !isSidebarOpen && 'lg:justify-center lg:px-2',
                )}
              >
                <Avatar className="h-8 w-8 border border-muted-foreground/20 bg-background">
                  {user?.profileImage ? (
                    <AvatarImage src={user.profileImage} alt={displayName ?? 'User'} />
                  ) : null}
                  <AvatarFallback className="bg-background text-foreground">
                    {getInitials(user?.email)}
                  </AvatarFallback>
                </Avatar>
                {isSidebarOpen && (
                  <div className="flex flex-1 flex-col items-start overflow-hidden">
                    <span className="truncate text-sm font-medium">
                      {displayName ?? t('header.unknown')}
                    </span>
                    {user?.email && (
                      <span className="truncate text-xs text-muted-foreground group-hover:text-accent-foreground">
                        {user.email}
                      </span>
                    )}
                  </div>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align={isSidebarOpen ? 'start' : 'end'}
              side="top"
              className="w-56"
            >
              <DropdownMenuLabel>{t('header.account')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {user?.role !== 'root' && (
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  {t('header.profile')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                {t('header.signOut')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
      <ConfirmationDialog
        open={isLogoutDialogOpen}
        onOpenChange={setIsLogoutDialogOpen}
        title={t('header.logoutDialog.title')}
        description={t('header.logoutDialog.description')}
        confirmText={t('header.logoutDialog.confirm')}
        cancelText={t('header.logoutDialog.cancel')}
        variant="destructive"
        onConfirm={handleLogoutConfirm}
      />
    </>
  );
}
